import { useContext } from 'react'
import { Loader2 } from 'lucide-react'
import {
  ActionDependant,
  GlobalDataContext,
  TemplateContext,
  View,
  evaluateTemplateValue,
} from '@ea-lab/reactive-json'

/**
 * Generic loading overlay : wraps content and, while `when` is truthy, dims it
 * with a translucent backdrop + spinner to signal an in-flight (or imminent)
 * update.
 *
 * Pairs with the `submitData` refetch pattern : the triggering element sets a
 * flag `true` on its event and resets it `on: response`. With `requestKey`
 * cancellation only the final request emits `response`, so the overlay stays
 * up for the whole burst of rapid clicks. A `SearchField` can set the same
 * flag the moment the user types, covering the debounce window too.
 *
 * YAML usage:
 *   - type: LoadingOverlay
 *     when: ~~.esLoading
 *     label: 'Mise à jour…'
 *     content:
 *       - type: Switch
 *         content: ~~.esResult.contents
 *         singleOption: { load: productCard }
 */

const isTruthy = (v) => {
  if (Array.isArray(v)) return v.length > 0
  if (v && typeof v === 'object') return Object.keys(v).length > 0
  return v !== '' && v != null && v !== false && v !== 'false' && v !== 0 && v !== '0'
}

export const LoadingOverlay = ({ props, path, currentData }) => {
  const globalDataContext = useContext(GlobalDataContext)
  const templateContext = useContext(TemplateContext)
  const resolve = (v) => evaluateTemplateValue({ valueToEvaluate: v, globalDataContext, templateContext })

  const active = isTruthy(resolve(props.when))
  const label = resolve(props.label)
  const wrapperClass = resolve(props.attributes?.class) ?? ''
  const overlayClass = resolve(props.overlayClass) ?? 'bg-white/60 backdrop-blur-[1px]'

  return (
    <ActionDependant {...props}>
      <div className={`relative ${wrapperClass}`} aria-busy={active || undefined}>
        {props.content != null && (
          <View props={props.content} path={`${path}.content`} datafield="content" currentData={currentData?.content} />
        )}
        {active && (
          <div className={`absolute inset-0 z-20 flex items-center justify-center transition-opacity ${overlayClass}`}>
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-md border border-gray-100 text-sm font-medium text-gray-600">
              <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
              {label && <span>{label}</span>}
            </span>
          </div>
        )}
      </div>
    </ActionDependant>
  )
}
